import {
    DecisionSupportTab,
    DecisionSupportTabs,
} from '@/components/decisionSupport/DecisionSupportTabs';
import { RecommendationSearch } from '@/components/decisionSupport/RecommendationSearch';
import { RecommendationsTable } from '@/components/decisionSupport/tables/RecommendationsTable';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import { Box } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

export enum RecommendationTab {
    RECOMMENDED,
    NOT_RECOMMENDED,
}

type DecisionSupportRecommendationsProps<T> = {
    recommended: T[];
    recommendedColumns: ColumnDef<T>[];
    notRecommended: T[];
    notRecommendedColumns: ColumnDef<T>[];

    getRowValue: (row: T) => unknown;
    selectedValue: unknown;
    onRowSelect: (row: T) => void;

    loading?: boolean;
    recommendedLabel?: string;
    notRecommendedLabel?: string;
    disabledRowValues?: Set<unknown>;

    // Label shown for each option in the search dropdown. When provided, a
    // unified autocomplete search across both groups is rendered.
    getOptionLabel?: (row: T) => string;
    // Text matched against the search query. Defaults to `getOptionLabel`.
    getSearchText?: (row: T) => string;

    // Plural noun for the items in the list
    itemNoun: string;

    // Which tab to open on first render. Defaults to RecommendationTab.RECOMMENDED.
    defaultTab?: RecommendationTab;

    // Fires whenever the user clicks a tab (does not fire for `defaultTab`).
    onTabChange?: (tab: RecommendationTab) => void;
};

export const DecisionSupportRecommendations = <T,>(
    props: DecisionSupportRecommendationsProps<T>,
) => {
    const {
        recommended,
        notRecommended,
        recommendedColumns,
        notRecommendedColumns,
        getRowValue,
        selectedValue,
        onRowSelect,
        loading = false,
        recommendedLabel = 'Recommended',
        notRecommendedLabel = 'Not Recommended',
        disabledRowValues,
        getOptionLabel,
        getSearchText,
        itemNoun,
        defaultTab = RecommendationTab.RECOMMENDED,
        onTabChange,
    } = props;

    const [activeTab, setActiveTab] = useState<RecommendationTab>(defaultTab);

    // Bumped on each search selection to drive the table's scroll-to-row. The
    // value identifies the target row; the nonce re-triggers the scroll even if
    // the same row is picked again.
    const [scrollSignal, setScrollSignal] = useState<{ value: unknown; nonce: number } | null>(
        null,
    );

    // Selecting from search jumps to the row: switch to its tab (Recommended vs
    // Not Recommended) if needed, then scroll it into view.
    const handleSearchSelect = useCallback(
        (row: T) => {
            const value = getRowValue(row);
            const inNotRecommended = notRecommended.some((r) => getRowValue(r) === value);
            setActiveTab(
                inNotRecommended
                    ? RecommendationTab.NOT_RECOMMENDED
                    : RecommendationTab.RECOMMENDED,
            );
            setScrollSignal((prev) => ({ value, nonce: (prev?.nonce ?? 0) + 1 }));
            onRowSelect(row);
        },
        [getRowValue, notRecommended, onRowSelect],
    );

    const tabs: DecisionSupportTab[] = useMemo(
        () => [
            {
                label: loading ? recommendedLabel : `${recommendedLabel} (${recommended.length})`,
            },
            {
                label: loading
                    ? notRecommendedLabel
                    : `${notRecommendedLabel} (${notRecommended.length})`,
            },
        ],
        [loading, recommendedLabel, notRecommendedLabel, recommended.length, notRecommended.length],
    );

    const activeTabProps = useMemo(() => {
        if (activeTab === RecommendationTab.RECOMMENDED) {
            return {
                data: recommended,
                columns: recommendedColumns,
            };
        }
        return {
            data: notRecommended,
            columns: notRecommendedColumns,
        };
    }, [activeTab, recommended, recommendedColumns, notRecommended, notRecommendedColumns]);

    const handleTabChange = useCallback(
        (index: number) => {
            setActiveTab(index);
            onTabChange?.(index);
        },
        [onTabChange],
    );

    const showSearch =
        !!getOptionLabel && !loading && recommended.length + notRecommended.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {showSearch && (
                <RecommendationSearch
                    recommended={recommended}
                    notRecommended={notRecommended}
                    getOptionLabel={getOptionLabel}
                    getSearchText={getSearchText}
                    getRowValue={getRowValue}
                    onSelect={handleSearchSelect}
                    disabledRowValues={disabledRowValues}
                />
            )}
            <DecisionSupportTabs tabs={tabs} activeIndex={activeTab} onChange={handleTabChange} />
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', mt: 2 }}>
                <RecommendationsTable
                    {...activeTabProps}
                    selectedValue={selectedValue}
                    getRowValue={getRowValue}
                    onRowSelect={onRowSelect}
                    loading={loading}
                    disabledRowValues={disabledRowValues}
                    scrollToValue={scrollSignal?.value}
                    scrollNonce={scrollSignal?.nonce}
                    emptyText={`No ${itemNoun} found`}
                    emptyIcon={<InboxOutlined fontSize='medium' />}
                />
            </Box>
        </Box>
    );
};
